import { Typography } from '@mui/material';
import Box from '@mui/material/Box';
import Step from '@mui/material/Step';
import StepButton from '@mui/material/StepButton';
import StepContent from '@mui/material/StepContent';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';
import { RenderCTA } from '../FormCTA/FormCTA.helpers';
import AppointmentReview, { AppointmentSubmitHandle } from '@src/components/AppointmentReview/AppointmentReview';
import Confirmation from '@src/components/Confirmation/Confirmation';
import DateAndTime from '@src/components/DateAndTime/DateAndTime';
import AdditionalPatientInformationForm from '@src/components/Forms/AdditionalPatientInformationForm/AdditionalPatientInformationForm';
import ConsolidatedPatientInformationForm from '@src/components/Forms/ConsolidatedPatientInformationForm/ConsolidatedPatientInformationForm';
import FullPatientInformationForm from '@src/components/Forms/FullPatientInformationForm/FullPatientInformationForm';
import GuarantorInformationForm from '@src/components/Forms/GuarantorInformationForm/GuarantorInformationForm';
import PatientInformationForm from '@src/components/Forms/PatientInformationForm/PatientInformationForm';
import { Card, SuccessCard } from '@src/lib/__generated/contentful-sdk';
import { useAppointmentStore, useFormSectionStore, usePatientInformationFormStore } from '@src/store';
import { slugify } from '@src/utils/slugify';
import { trimTimeZoneOffset } from '@src/utils/trimTimeZoneOffset';
interface MobileStepperProps {
  shouldUseMottoConfig?: boolean;
  specialFlow?: boolean;
  v2Flow?: boolean;
  successCardsCollection: {
    items: Array<SuccessCard>;
  };
  marketingContent: {
    cardsCollection: {
      items: Array<Card>;
    };
  };
}
export default function MobileStepper({
  shouldUseMottoConfig = false,
  specialFlow = false,
  v2Flow = false,
  successCardsCollection,
  marketingContent
}: Readonly<MobileStepperProps>) {
  const activeSectionIndex = useFormSectionStore(store => store.activeSectionIndex);
  const availableFormNodes = useFormSectionStore(store => store.availableFormNodes);
  const formNodes = useFormSectionStore(store => store.formNodes);
  const setActiveSection = useFormSectionStore(store => store.setActiveSection);
  const maxIndexReached = useFormSectionStore(store => store.maxIndexReached);
  const lockForm = useFormSectionStore(store => store.lockForm);
  const currentFormNodes = availableFormNodes();
  const firstName = usePatientInformationFormStore(store => store.firstName);
  const lastName = usePatientInformationFormStore(store => store.lastName);
  const appointmentSlot = useAppointmentStore(store => store.appointmentSlot);
  const {
    t
  } = useTranslation('stepper');
  const router = useRouter();
  const {
    locale
  } = router;
  const handleStepSelction = (index: number) => {
    setActiveSection(index);
  };
  const confirmationString = `${t('confirmationString')}${firstName}`;
  const childRef = useRef<AppointmentSubmitHandle>(null);
  useEffect(() => {
    setTimeout(() => window.scrollTo(0, 0), 0);
  }, [activeSectionIndex]);
  const determineStepSubtext = (stepName: string) => {
    if (stepName === 'Patient Information') {
      return <Typography data-private variant="caption">
					{firstName} {lastName}
				</Typography>;
    }
    if (stepName === 'Date & Time' && appointmentSlot?.time) {
      const formattedAppointmentDate = new Intl.DateTimeFormat(locale, {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      }).format(new Date(appointmentSlot?.time));
      const appointmentStartTime = new Intl.DateTimeFormat(locale, {
        timeStyle: 'short'
      }).format(new Date(trimTimeZoneOffset(appointmentSlot?.time) ?? ''));
      return <Typography variant="caption" data-private>
					{formattedAppointmentDate} - {appointmentStartTime}
				</Typography>;
    }
    return null;
  };
  const confirmAppointment = () => {
    childRef?.current?.handleSubmit();
  };
  return <>
			<Box sx={{
      padding: 1
    }} data-sentry-element="Box" data-sentry-source-file="Stepper.tsx">
				<Box sx={{
        textAlign: 'center'
      }} data-sentry-element="Box" data-sentry-source-file="Stepper.tsx">
					<Typography color="success.dark" variant="overline" data-sentry-element="Typography" data-sentry-source-file="Stepper.tsx">
						{!specialFlow && activeSectionIndex === 5 || specialFlow && activeSectionIndex === 4 || v2Flow && activeSectionIndex === 4 ? t('apptScheduled') : t('apptAssistant')}
					</Typography>
				</Box>
				{locale === 'en-US' && <Stepper activeStep={activeSectionIndex} orientation="vertical" sx={{
        '.MuiCollapse-root': {
          transitionDuration: '0s !important'
        }
      }}>
						{currentFormNodes.map((step, index) => <Step key={index} index={step.index} completed={step.completed || formNodes[activeSectionIndex].name === 'Confirmation'}>
								<StepButton disabled={lockForm && step.name !== 'Confirmation'} onClick={() => maxIndexReached >= step.index ? handleStepSelction(step.index) : null} optional={determineStepSubtext(step.name)} tabIndex={lockForm && step.name !== 'Confirmation' ? 0 : -1}>
									<StepLabel sx={{
              opacity: lockForm && step.name !== 'Confirmation' ? 0.5 : 1,
              '.MuiSvgIcon-root.Mui-completed': {
                color: 'secondary.main'
              },
              '.MuiSvgIcon-root.Mui-active': {
                color: 'secondary.main'
              }
            }} StepIconProps={{
              icon: index + 1
            }}>
										<Typography variant="bodyMediumBook" color={formNodes[activeSectionIndex].name === step.name ? 'text.primary' : step.completed ? 'text.interactive' : 'text.tertiary'}>
											{step.name}
										</Typography>
									</StepLabel>
								</StepButton>
								<StepContent sx={{
            margin: activeSectionIndex === step.index ? '0.8rem 0 2rem -1rem' : ''
          }} TransitionProps={{
            unmountOnExit: false
          }}>
									{/* default user flow */}
									{!specialFlow && !v2Flow && activeSectionIndex === 0 && <PatientInformationForm formId={slugify(step.name)} />}
									{!specialFlow && !v2Flow && activeSectionIndex === 1 && <DateAndTime />}
									{!specialFlow && !v2Flow && activeSectionIndex === 2 && <AdditionalPatientInformationForm formId={slugify(step.name)} shouldUseMottoConfig={shouldUseMottoConfig} />}
									{!specialFlow && !v2Flow && activeSectionIndex === 3 && <GuarantorInformationForm formId={slugify(step.name)} />}
									{!specialFlow && !v2Flow && activeSectionIndex === 4 && <>
											<Typography data-test-id="form-header" variant="header2" mt={-2} mb={2}>
												{t('reviewConfirmAppt')}
											</Typography>
											<AppointmentReview ref={childRef} />
										</>}
									{!specialFlow && !v2Flow && activeSectionIndex === 5 && <>
											<Typography data-test-id="form-header" variant="header2" mt={-2} mb={2} data-private>
												{confirmationString}
											</Typography>
											<Confirmation successCardsCollection={successCardsCollection} marketingContent={marketingContent} />
										</>}

									{/* special Motto flow */}
									{specialFlow && activeSectionIndex === 0 && <DateAndTime />}
									{specialFlow && activeSectionIndex === 1 && <FullPatientInformationForm formId={slugify(step.name)} shouldUseMottoConfig={shouldUseMottoConfig} />}
									{specialFlow && activeSectionIndex === 2 && <GuarantorInformationForm formId={slugify(step.name)} />}
									{specialFlow && activeSectionIndex === 3 && <>
											<Typography data-test-id="form-header" variant="header2" mt={-2} mb={2}>
												{t('reviewConfirmAppt')}
											</Typography>
											<AppointmentReview ref={childRef} />
										</>}
									{specialFlow && activeSectionIndex === 4 && <>
											<Typography data-test-id="form-header" variant="header2" mt={-2} mb={2} data-private>
												{confirmationString}
											</Typography>
											<Confirmation successCardsCollection={successCardsCollection} marketingContent={marketingContent} />
										</>}

									{/* V2 A/B test flow — consolidated Patient Info tab */}
									{v2Flow && activeSectionIndex === 0 && <ConsolidatedPatientInformationForm formId={slugify(step.name)} shouldUseMottoConfig={shouldUseMottoConfig} />}
									{v2Flow && activeSectionIndex === 1 && <DateAndTime />}
									{v2Flow && activeSectionIndex === 2 && <GuarantorInformationForm formId={slugify(step.name)} />}
									{v2Flow && activeSectionIndex === 3 && <>
											<Typography data-test-id="form-header" variant="header2" mt={-2} mb={2}>
												{t('reviewConfirmAppt')}
											</Typography>
											<AppointmentReview ref={childRef} />
										</>}
									{v2Flow && activeSectionIndex === 4 && <>
											<Typography data-test-id="form-header" variant="header2" mt={-2} mb={2} data-private>
												{confirmationString}
											</Typography>
											<Confirmation successCardsCollection={successCardsCollection} marketingContent={marketingContent} />
										</>}
								</StepContent>
							</Step>)}
					</Stepper>}
				{locale === 'es-US' && <Stepper activeStep={activeSectionIndex} orientation="vertical" sx={{
        '.MuiCollapse-root': {
          transitionDuration: '0s !important'
        }
      }}>
						{currentFormNodes.map((step, index) => <Step key={index} index={step.index} completed={step.completed || formNodes[activeSectionIndex].name === 'Confirmation'}>
								<StepButton disabled={lockForm && step.name !== 'Confirmation'} onClick={() => maxIndexReached >= step.index ? handleStepSelction(step.index) : null} optional={determineStepSubtext(step.name)} tabIndex={lockForm && step.name !== 'Confirmation' ? 0 : -1}>
									<StepLabel sx={{
              opacity: lockForm && step.name !== 'Confirmation' ? 0.5 : 1,
              '.MuiSvgIcon-root.Mui-completed': {
                color: 'secondary.main'
              },
              '.MuiSvgIcon-root.Mui-active': {
                color: 'secondary.main'
              }
            }} StepIconProps={{
              icon: index + 1
            }}>
										<Typography variant="bodyMediumBook" color={formNodes[activeSectionIndex].name === step.name ? 'text.primary' : step.completed ? 'text.interactive' : 'text.tertiary'}>
											{step.nameES}
										</Typography>
									</StepLabel>
								</StepButton>
								<StepContent sx={{
            margin: activeSectionIndex === step.index ? '0.8rem 0 2rem -1rem' : ''
          }} TransitionProps={{
            unmountOnExit: false
          }}>
									{/* default user flow */}
									{!specialFlow && !v2Flow && activeSectionIndex === 0 && <PatientInformationForm formId={slugify(step.name)} />}
									{!specialFlow && !v2Flow && activeSectionIndex === 1 && <DateAndTime />}
									{!specialFlow && !v2Flow && activeSectionIndex === 2 && <AdditionalPatientInformationForm formId={slugify(step.name)} shouldUseMottoConfig={shouldUseMottoConfig} />}
									{!specialFlow && !v2Flow && activeSectionIndex === 3 && <GuarantorInformationForm formId={slugify(step.name)} />}
									{!specialFlow && !v2Flow && activeSectionIndex === 4 && <>
											<Typography data-test-id="form-header" variant="header2" mt={-2} mb={2}>
												{t('reviewConfirmAppt')}
											</Typography>
											<AppointmentReview ref={childRef} />
										</>}
									{!specialFlow && !v2Flow && activeSectionIndex === 5 && <>
											<Typography data-test-id="form-header" variant="header2" mt={-2} mb={2} data-private>
												{confirmationString}
											</Typography>
											<Confirmation successCardsCollection={successCardsCollection} marketingContent={marketingContent} />
										</>}

									{/* special Motto flow */}
									{specialFlow && activeSectionIndex === 0 && <DateAndTime />}
									{specialFlow && activeSectionIndex === 1 && <FullPatientInformationForm formId={slugify(step.name)} shouldUseMottoConfig={shouldUseMottoConfig} />}
									{specialFlow && activeSectionIndex === 2 && <GuarantorInformationForm formId={slugify(step.name)} />}
									{specialFlow && activeSectionIndex === 3 && <>
											<Typography data-test-id="form-header" variant="header2" mt={-2} mb={2}>
												{t('reviewConfirmAppt')}
											</Typography>
											<AppointmentReview ref={childRef} />
										</>}
									{specialFlow && activeSectionIndex === 4 && <>
											<Typography data-test-id="form-header" variant="header2" mt={-2} mb={2} data-private>
												{confirmationString}
											</Typography>
											<Confirmation successCardsCollection={successCardsCollection} marketingContent={marketingContent} />
										</>}

									{/* V2 A/B test flow — consolidated Patient Info tab */}
									{v2Flow && activeSectionIndex === 0 && <ConsolidatedPatientInformationForm formId={slugify(step.name)} shouldUseMottoConfig={shouldUseMottoConfig} />}
									{v2Flow && activeSectionIndex === 1 && <DateAndTime />}
									{v2Flow && activeSectionIndex === 2 && <GuarantorInformationForm formId={slugify(step.name)} />}
									{v2Flow && activeSectionIndex === 3 && <>
											<Typography data-test-id="form-header" variant="header2" mt={-2} mb={2}>
												{t('reviewConfirmAppt')}
											</Typography>
											<AppointmentReview ref={childRef} />
										</>}
									{v2Flow && activeSectionIndex === 4 && <>
											<Typography data-test-id="form-header" variant="header2" mt={-2} mb={2} data-private>
												{confirmationString}
											</Typography>
											<Confirmation successCardsCollection={successCardsCollection} marketingContent={marketingContent} />
										</>}
								</StepContent>
							</Step>)}
					</Stepper>}
			</Box>

			<RenderCTA name={formNodes[activeSectionIndex].name} confirmAppointment={confirmAppointment} data-sentry-element="RenderCTA" data-sentry-source-file="Stepper.tsx" />
		</>;
}